// shop-frontend/src/pages/ShopMerchantAgreement.jsx
// Moondala Merchant (Shop) Agreement.
// NOTE: This is a first draft of the legal terms. It MUST be reviewed by a
// qualified payments/fintech attorney before being relied upon — especially
// the "agent of the payee" / payment-collection sections, which are what
// support Moondala's position that it is not acting as a money transmitter.
import React from "react";
import { Link } from "react-router-dom";

export default function ShopMerchantAgreement() {
  return (
    <div style={{ maxWidth: 820, margin: "0 auto", padding: "32px 20px 64px", lineHeight: 1.6 }}>
      <Link to="/shop/login" style={{ display: "inline-block", marginBottom: 20 }}>
        ← Back
      </Link>

      <h1>Moondala Merchant Agreement</h1>
      <p style={{ opacity: 0.7 }}>Last updated: June 25, 2026</p>

      <p>
        This Merchant Agreement (the &ldquo;Agreement&rdquo;) is entered into between
        Moondala, Inc. (&ldquo;Moondala,&rdquo; &ldquo;we,&rdquo; &ldquo;us&rdquo;) and the
        seller registering a shop on the Moondala platform (&ldquo;Merchant,&rdquo;
        &ldquo;you&rdquo;). By creating a shop and clicking &ldquo;I agree&rdquo; at
        registration, you accept this Agreement.
      </p>

      <h2>1. The Marketplace</h2>
      <p>
        Moondala operates an online marketplace that lets Merchants list and sell their own
        products to buyers. Moondala does not own the products listed by Merchants and is not
        the seller of those products. Each Merchant is the seller of record for its own
        listings and is solely responsible for its products, fulfillment, descriptions, taxes,
        and compliance with applicable law.
      </p>

      <h2>2. Appointment of Moondala as Limited Payment Agent</h2>
      <p>
        You appoint Moondala as your <strong>limited agent solely for the purpose of
        collecting, holding, and settling payments</strong> from buyers on your behalf in
        connection with sales made through your shop.
      </p>
      <ul>
        <li>
          A buyer&rsquo;s payment of the purchase price to Moondala (or a payment processor
          engaged by Moondala) is considered <strong>payment made directly to you</strong>, and
          satisfies the buyer&rsquo;s payment obligation to you in full, regardless of whether
          Moondala actually remits the funds to you.
        </li>
        <li>
          Moondala receives buyer funds <strong>as your agent</strong>, not as a counterparty
          to the buyer. You bear the risk of Moondala&rsquo;s failure to remit only as a matter
          of contract between you and Moondala; the buyer&rsquo;s obligation is discharged on
          payment.
        </li>
        <li>
          This appointment does not make Moondala a buyer, reseller, guarantor, or money
          transmitter with respect to your transactions.
        </li>
      </ul>

      <h2>3. Platform Fee</h2>
      <p>
        Moondala charges a platform commission of <strong>six percent (6%)</strong> of the
        order value on completed sales. This fee is deducted from amounts collected on your
        behalf before settlement. A portion of Moondala&rsquo;s platform fee may be shared with
        members of Moondala&rsquo;s referral program; such payments are made entirely from
        Moondala&rsquo;s own fee revenue and never from your settlement amount beyond the stated
        6%.
      </p>

      <h2>4. Settlement &amp; Payouts</h2>
      <ul>
        <li>
          After deducting the platform fee and any applicable refunds, chargebacks, or
          adjustments, Moondala settles the remaining proceeds to you (&ldquo;Payout&rdquo;).
        </li>
        <li>
          Payouts may be held for a reasonable period to cover refund and chargeback risk
          before becoming available for withdrawal.
        </li>
        <li>
          Payouts may be made through supported rails, which may include bank transfer,
          third-party payment providers, or stablecoin (e.g., USDC on the Base network), based
          on availability in your country and the method you select.
        </li>
        <li>
          Where settlement involves currency conversion, the applicable exchange rate and a
          conversion buffer/processing fee will be disclosed before you confirm a withdrawal.
        </li>
      </ul>

      <h2>5. Referral Program</h2>
      <p>
        Moondala operates a referral program in which members may earn a share of
        Moondala&rsquo;s platform fee when sales occur within their referral network, across
        multiple referral levels. Earnings under the referral program arise <strong>only from
        completed sales of real products</strong>. There is no fee to join, and no compensation
        is paid for recruitment itself. The referral program is funded exclusively from
        Moondala&rsquo;s 6% platform fee.
      </p>

      <h2>6. Merchant Obligations</h2>
      <ul>
        <li>You will provide accurate business and owner information and keep it current.</li>
        <li>You will only list products you are legally entitled to sell.</li>
        <li>You are responsible for your own taxes, licensing, and regulatory compliance.</li>
        <li>You will fulfill orders and honor your stated return/refund policy.</li>
      </ul>

      <h2>7. Refunds, Chargebacks &amp; Reserves</h2>
      <p>
        You authorize Moondala to deduct, offset, or recover from your balance or future
        Payouts any refunds, chargebacks, fees, or amounts owed under this Agreement, and to
        hold reserves where reasonably necessary to cover such risk.
      </p>

      <h2>8. Term &amp; Termination</h2>
      <p>
        Either party may terminate this Agreement at any time. Upon termination, Moondala will
        settle undisputed amounts owed to you, subject to outstanding refunds, chargebacks, and
        reserves.
      </p>

      <h2>9. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, Moondala is not liable for indirect,
        incidental, or consequential damages arising from your use of the platform.
      </p>

      <h2>10. Changes to this Agreement</h2>
      <p>
        We may update this Agreement from time to time. Continued use of the platform after an
        update constitutes acceptance of the revised terms.
      </p>

      <p style={{ marginTop: 32, opacity: 0.7 }}>
        Questions? Contact us at support@moondala.one.
      </p>
    </div>
  );
}
